import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { fetchInitialData, hasRecentInteractions } from '@fiverr-private/send_message';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { isNil } from 'lodash';

const redirect = (url) => window.location.assign(url);

export const FirstMessagePopup = ({ onClose, modalTriggered, sellerName }) => {
    const firstMessagePackageRef = useRef();

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState();

    const loadPopup = async() => {
        try {
            const [, data] = await Promise.all([lazyLoadModal(), fetchModalData()]);
            if (hasRecentInteractions(data.sender.username, sellerName)) {
                redirectToInboxConversation();
                return;
            }

            openModal();
        } catch (error) {
            logger.error(error, { description: 'Failed to initialize the First Message Popup' });
            redirectToInboxConversation();
        }
    };

    const lazyLoadModal = async() => {
        try {
            firstMessagePackageRef.current = await import(/* webpackChunkName: 'SendMessagePopup' */'@fiverr-private/send_message');
        } catch (error) {
            logger.error(error, { description: 'Failed to lazy load @fiverr-private/send_message package.' });
            throw error;
        }
    };

    const fetchModalData = async() => {
        if (!isNil(modalData)) {
            return modalData;
        }

        try {
            const data = await fetchInitialData(sellerName);
            setModalData(data);
            return data;
        } catch (error) {
            logger.error(error, { description: 'Failed to fetch Send Message initial data' });
            throw error;
        }
    };

    const redirectToInboxConversation = () => {
        const url = pathfinder('conversation', { id: sellerName });

        redirect(url);
    };

    const openModal = () => setShowModal(true);

    const handleModalClose = (event) => {
        setShowModal(false);
        onClose(event);
    };

    const modalHasBeenLoaded = !isNil(firstMessagePackageRef.current);
    const Modal = modalHasBeenLoaded ? firstMessagePackageRef.current.ControlledSendMessageModal : EmptyComponent;
    const show = showModal && !isNil(modalData);

    useEffect(() => {
        if (modalTriggered) {
            loadPopup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalTriggered]);

    return (
        <Modal show={show}
            onClose={handleModalClose}
            recipientInfo={modalData?.recipient}
            showInfo/>
    );
};

FirstMessagePopup.propTypes = {
    onClose: PropTypes.func,
    modalTriggered: PropTypes.bool,
    sellerName: PropTypes.string.isRequired
};

const EmptyComponent = () => null;
